// Rechazos del cierre de la venta de accesorios → qué hace el vendedor. Un `code` por acción
// distinta (diseño 2026-10-20 §4). Puro.
import { RequestTimeoutError } from '@/api/client';
import { ApiError, INTERNAL_ERROR_MESSAGE } from '@/api/errors';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { salePaymentErrorMessage } from '@/domains/sales/wizard/salePayments';

const EXTERNAL_DOWN = 'No pudimos completar la operación en este momento. Reintenta.';
const VALIDATION = 'Revisa los datos capturados: el sistema los rechazó.';

function dataOf(error: ApiError): Record<string, unknown> {
    return (error.data ?? {}) as Record<string, unknown>;
}

function numberIn(data: Record<string, unknown>, ...keys: string[]): number | undefined {
    for (const key of keys) {
        const value = data[key];
        if (typeof value === 'number') return value;
    }
    return undefined;
}

// `reload` = releer existencias y precios; `retry` = reintentar con la misma llave.
export type SaleRecovery = 'remove-line' | 'reload' | 'retry' | 'none';

export interface SaleFailure {
    message: string;
    recovery: SaleRecovery;
    /** SKU que el silo no encontró, para quitarlo del carrito. */
    sku?: string;
    /** Total que calculó el silo, cuando el precio cambió. */
    expectedCents?: number;
}

const REFUND_HINT = 'Si ya cobraste, devuelve el cobro al cliente.';

function amountMismatch(error: ApiError): SaleFailure {
    const expectedCents = numberIn(dataOf(error), 'expected', 'expectedCents');
    const total =
        expectedCents === undefined
            ? 'El precio de algún artículo cambió.'
            : `El precio cambió: el total ahora es ${formatMoneyCents(expectedCents)}.`;
    return {
        message: `${total} Revisa el carrito antes de cobrar.`,
        recovery: 'reload',
        expectedCents,
    };
}

/** Falla al completar la venta. */
export function saleFailure(error: unknown): SaleFailure {
    if (error instanceof RequestTimeoutError) {
        return {
            message: 'La venta tardó demasiado en responder. Reintenta: no se cobra dos veces.',
            recovery: 'retry',
        };
    }
    if (!(error instanceof ApiError)) {
        return { message: 'No se pudo completar la venta. Reintenta.', recovery: 'retry' };
    }
    const detailMessage = salePaymentErrorMessage(error);
    if (detailMessage) return { message: detailMessage, recovery: 'none' };
    switch (error.code) {
        case 'PRODUCT_NOT_SELLABLE':
        case 'PHONE_REQUIRES_WIZARD': {
            const sku = dataOf(error).sku;
            const message =
                'Los teléfonos y los kits con teléfono no se venden aquí: véndelos desde Nueva venta › Teléfono.';
            return typeof sku === 'string'
                ? { message: `${message} Lo quitamos del carrito.`, recovery: 'remove-line', sku }
                : { message, recovery: 'reload' };
        }
        case 'IDENTIFIER_MISMATCH':
        case 'IDENTIFIER_NOT_AVAILABLE':
            return {
                message:
                    'Una de las series no corresponde a una pieza disponible de la tienda. Revisa las series.',
                recovery: 'none',
            };
        case 'COMPONENT_OUT_OF_STOCK':
            return {
                message: `Ya no hay existencias suficientes de algún artículo. ${REFUND_HINT}`,
                recovery: 'reload',
            };
        case 'CATALOG_UNAVAILABLE':
            return { message: EXTERNAL_DOWN, recovery: 'retry' };
        case 'VALIDATION_ERROR':
            return { message: VALIDATION, recovery: 'none' };
        case 'PRODUCT_NOT_FOUND': {
            const sku = dataOf(error).sku;
            return typeof sku === 'string'
                ? {
                      message: `El artículo ${sku} ya no está en el catálogo de la tienda. Lo quitamos del carrito.`,
                      recovery: 'remove-line',
                      sku,
                  }
                : {
                      message:
                          'Un artículo ya no está en el catálogo de la tienda. Revisa el carrito.',
                      recovery: 'reload',
                  };
        }
        case 'INSUFFICIENT_STOCK':
            return {
                message: `Ya no hay existencias suficientes de algún artículo. ${REFUND_HINT}`,
                recovery: 'reload',
            };
        case 'PAYMENT_AMOUNT_MISMATCH':
            return amountMismatch(error);
        case 'IDEMPOTENCY_CONFLICT':
            return {
                message: 'Esta venta todavía se está procesando. Espera unos segundos y reintenta.',
                recovery: 'retry',
            };
        case 'DUPLICATE_FOLIO':
            return { message: 'No se pudo asignar el folio. Reintenta.', recovery: 'retry' };
        case 'EXTERNAL_INTEGRATION_ERROR':
            return { message: EXTERNAL_DOWN, recovery: 'retry' };
        case 'INTERNAL_ERROR':
            return { message: INTERNAL_ERROR_MESSAGE, recovery: 'retry' };
        default:
            return {
                message:
                    error.httpStatus >= 500
                        ? INTERNAL_ERROR_MESSAGE
                        : error.msg || 'No se pudo completar la venta. Reintenta.',
                recovery: 'retry',
            };
    }
}
